import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { Expand, MessagesSquare, Plus, Shrink, Sparkles } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ElementType, PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/components/ui/cn';
import { clampAiPanelWidth, resolveAiPanelWidth, useUIStore } from '@shared/stores/ui.store';

import { usePickerPolicy } from '../data-policy/picker-policy';
import { useChat } from '../hooks/use-chat';
import { initAiCio } from '../init';
import { useConversationsStore } from '../store/conversations.store';

import { ChatHistory } from './ChatHistory';
import { ChatThread } from './ChatThread';
import { Composer } from './Composer';
import { DataPolicyDialogHost } from './DataPolicyDialog';
import { TokenMeter } from './TokenMeter';

const AI_CIO_PATH = '/ai-cio';

export function AiCioPanel() {
  const { t, i18n } = useTranslation('ai-cio');
  const isRtl = i18n.dir() === 'rtl';
  const { aiPanelOpen, aiPanelShowHistory, aiPanelWidth, setAiPanelShowHistory, setAiPanelWidth } =
    useUIStore();
  const backPanelWidth = useUIStore((s) => s.backPanelWidth);
  const backPanelFront = useUIStore((s) => s.backPanelFront);
  const aiPanelExpandedWidth = useUIStore((s) => s.aiPanelExpandedWidth);

  const {
    conversation,
    isEmpty,
    historyState,
    modelId,
    modelName,
    sessionModelUnknown,
    connected,
    thinking,
    setThinking,
    agentic,
    setAgentic,
    send,
    setModelId,
    setFeedback,
    regenerate,
    answerInteraction,
    decideProposal,
    busy,
    stopping,
    following,
    compacting,
    meter,
    compactedThroughTurn,
    compactionNote,
    compactOff,
    compactHidden,
    compact,
    dismissCompactionNote,
    stop,
    removeQueued,
    takeBackQueued,
    sendQueuedNow,
    waitingForPolicy,
    chatHasInvestorData,
    answerWithInvestbanqModel,
    startNewChatWith,
  } = useChat();
  const startNewChatFromPicker = useCallback(
    (id: string) => {
      startNewChatWith(id);
    },
    [startNewChatWith],
  );
  const pickerPolicy = usePickerPolicy({
    chatHasInvestorData,
    onStartNewChat: startNewChatFromPicker,
  });

  // ── Drag-to-resize (widen up to 1/3 of the screen; never below default) ──
  const draggingRef = useRef(false);
  const startResize = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      draggingRef.current = true;
      const onMove = (ev: PointerEvent) => {
        if (!draggingRef.current) return;
        // Distance from the docked edge → desired width, clamped to [default, 1/3].
        setAiPanelWidth(clampAiPanelWidth(isRtl ? ev.clientX : window.innerWidth - ev.clientX));
        // A manual resize always wins over a back panel's carried-over width.
        if (useUIStore.getState().aiPanelExpandedWidth !== 0) {
          useUIStore.setState({ aiPanelExpandedWidth: 0 });
        }
      };
      const onUp = () => {
        draggingRef.current = false;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        document.body.style.userSelect = '';
      };
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [isRtl, setAiPanelWidth],
  );

  // Keep the width within the 1/3 cap if the window shrinks.
  useEffect(() => {
    if (!aiPanelOpen) return;
    const onResize = () => {
      const clamped = clampAiPanelWidth(aiPanelWidth);
      if (clamped !== aiPanelWidth) setAiPanelWidth(clamped);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [aiPanelOpen, aiPanelWidth, setAiPanelWidth]);

  // Expand to cover the back panel when it is open, so it is not closed but
  // simply overlaid. Once the back panel closes, `aiPanelExpandedWidth` keeps
  // that width instead of snapping back down. A manual drag still wins when
  // it produces something wider than both.
  const width = resolveAiPanelWidth(aiPanelWidth, backPanelWidth, aiPanelExpandedWidth);

  return (
    <>
      <DataPolicyDialogHost />
      <AnimatePresence>
        {aiPanelOpen && (
          <>
            {/* Panel */}
            <motion.div
              key="ai-panel"
              data-ai-cio-surface
              initial={{ x: isRtl ? '-100%' : '100%' }}
              animate={{ x: 0 }}
              exit={{ x: isRtl ? '-100%' : '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 34 }}
              style={{ width }}
              // Sits below the top tab bar (top-14) so the bar + AI trigger stay
              // visible; no backdrop — the dashboard reflows to reserve this space.
              // Normally z-40, above back panels (z-30), so opening AI CIO covers
              // an already-open detail drawer instead of closing it. But if a back
              // panel was opened MORE RECENTLY than AI CIO, it should stay visible
              // instead of vanishing under this panel — drop below its z-30 then.
              className={cn(
                'fixed bottom-0 end-0 top-14 flex flex-col border-s border-t bg-background shadow-xl',
                backPanelFront ? 'z-20' : 'z-40',
              )}
            >
              {/* Drag handle (inline-start edge) */}
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label={t('panel.resize', 'Resize panel')}
                onPointerDown={startResize}
                className="group absolute inset-y-0 start-0 z-10 w-1.5 cursor-col-resize"
              >
                <div className="absolute inset-y-0 start-0 w-px bg-border transition-colors group-hover:bg-primary/40" />
              </div>

              {/* Body — no header bar (per design); new-chat / history / expand /
                  close live in the floating AI trigger's hover menu + Escape. */}
              {aiPanelShowHistory ? (
                <ChatHistory
                  showSearch
                  className="min-h-0 flex-1"
                  onSelect={() => {
                    setAiPanelShowHistory(false);
                  }}
                />
              ) : (
                <div className="flex min-h-0 flex-1 flex-col">
                  {isEmpty || !conversation ? (
                    <div className="min-h-0 flex-1 overflow-y-auto py-4 ps-4">
                      <p className="pe-4 text-sm text-foreground">
                        {t('greeting.panelTitle', 'Hi, how can I help you today?')}
                      </p>
                    </div>
                  ) : conversation.messages.length === 0 ? (
                    <div className="min-h-0 flex-1 overflow-y-auto py-4 ps-4">
                      <p
                        className="pe-4 text-sm text-muted-foreground"
                        role={historyState === 'error' ? 'alert' : 'status'}
                      >
                        {historyState === 'error'
                          ? t('history.loadFailed', "Couldn't load this conversation's history.")
                          : t('history.loading', 'Loading conversation…')}
                      </p>
                    </div>
                  ) : (
                    <ChatThread
                      conversation={conversation}
                      onFeedback={setFeedback}
                      onRegenerate={regenerate}
                      onAnswerInteraction={answerInteraction}
                      onDecideProposal={decideProposal}
                      busy={busy}
                      following={following}
                      stopping={stopping}
                      compactedThroughTurn={compactedThroughTurn}
                      onRemoveQueued={removeQueued}
                      onSendQueuedNow={sendQueuedNow}
                      onAnswerInternal={answerWithInvestbanqModel}
                      onAskInNewChat={() => {
                        startNewChatWith(modelId);
                      }}
                      className="min-h-0 flex-1 py-4 ps-4"
                    />
                  )}
                  <div className="shrink-0 p-3">
                    <Composer
                      modelId={modelId}
                      sessionModelUnknown={sessionModelUnknown}
                      connected={connected}
                      onModelChange={setModelId}
                      onSend={send}
                      thinking={thinking}
                      onThinkingChange={setThinking}
                      agentic={agentic}
                      onAgenticChange={setAgentic}
                      conversationId={conversation?.id}
                      modelName={modelName}
                      busy={busy}
                      stopping={stopping}
                      compacting={compacting}
                      meter={
                        meter ? (
                          <TokenMeter
                            figure={meter}
                            busy={busy}
                            compacting={compacting}
                            off={compactOff}
                            withoutControl={connected || compactHidden}
                            onCompact={compact}
                          />
                        ) : undefined
                      }
                      compactionNote={compactionNote}
                      onCompactionNoteDismiss={dismissCompactionNote}
                      onStop={stop}
                      onTakeBack={takeBackQueued}
                      autoFocusInput={isEmpty || !conversation}
                      policy={pickerPolicy}
                      waitingForPolicy={waitingForPolicy}
                    />
                    <p className="mt-1.5 text-center text-[10px] text-muted-foreground/60">
                      {t(
                        'panel.disclaimer',
                        'AI CIO may make mistakes. Verify important information.',
                      )}
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// ── Sub-button with tooltip (hover menu) ─────────────────────────────────────

interface SubButtonProps {
  icon: ElementType;
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  index: number;
  onEnter: () => void;
  onLeave: () => void;
}

interface SubButtonSpec extends Omit<SubButtonProps, 'index' | 'onEnter' | 'onLeave'> {
  id: 'start' | 'recentChats' | 'shrink' | 'widen';
}

function SubButton({
  icon: Icon,
  label,
  disabled,
  onClick,
  index,
  onEnter,
  onLeave,
}: SubButtonProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <motion.div
      className="absolute start-0"
      style={{ top: (index + 1) * 44 }}
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.7 }}
      transition={{ delay: index * 0.06, type: 'spring', stiffness: 380, damping: 26 }}
      onMouseEnter={() => {
        setShowTooltip(true);
        onEnter();
      }}
      onMouseLeave={() => {
        setShowTooltip(false);
        onLeave();
      }}
    >
      <AnimatePresence>
        {showTooltip && (
          <motion.span
            initial={{ opacity: 0, x: 6 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 6 }}
            transition={{ duration: 0.12, ease: 'easeOut' }}
            className="pointer-events-none absolute end-full top-1/2 me-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-primary px-3 py-1.5 text-[11px] font-medium text-primary-foreground"
          >
            {label}
          </motion.span>
        )}
      </AnimatePresence>

      <button
        type="button"
        onClick={disabled ? undefined : onClick}
        className={cn(
          'flex size-9 items-center justify-center rounded-full border bg-background text-foreground shadow-[0px_1px_1px_rgba(0,0,0,0.05)] transition-colors',
          disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-accent',
        )}
        aria-label={label}
      >
        <Icon className="size-4" />
      </button>
    </motion.div>
  );
}

// ── AI CIO Trigger button (top-right; route-aware hover menu) ─────────────────

export function AiCioTrigger() {
  const { t } = useTranslation('ai-cio');
  const location = useLocation();
  const navigate = useNavigate();
  const onMainPage = location.pathname === AI_CIO_PATH;

  const { aiPanelOpen, openAiPanel, closeAiPanel, setAiPanelShowHistory, setAiCioPageHistoryOpen } =
    useUIStore();
  const newChat = useConversationsStore((s) => s.newChat);
  const isChatEmpty = useConversationsStore((s) => s.isActiveEmpty());

  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The trigger is always mounted inside the authenticated shell, so this is a
  // safe one-time spot to activate the real orchestrator backend + history sync.
  // (The "new chat" request seam is wired at module level in initAiCio so it
  // survives this component remounting when the tab bar changes layout.)
  useEffect(() => {
    initAiCio();
  }, []);

  // On the main page the resize sub-button means "Shrink" (→ open panel on
  // dashboard); in the panel context it means "Widen" (→ full page).
  // The sub-buttons are hover-only (per spec: "appear when hovering over the AI
  // icon"), so they're never pinned open just because the panel is open.
  const showSub = isOpen;

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  }, [cancelClose]);

  function handleMainEnter() {
    cancelClose();
    setIsOpen(true);
  }

  // Main page: clicking the icon does nothing (per spec). Off the main page:
  // clicking the icon opens or closes the panel (per the interaction matrix).
  function handleMainClick() {
    if (onMainPage) return;
    if (aiPanelOpen) {
      closeAiPanel();
      setIsOpen(false);
      cancelClose();
    } else {
      cancelClose();
      openAiPanel();
    }
  }

  const subButtons: SubButtonSpec[] = [
    {
      id: 'start',
      icon: Plus,
      label: t('panel.start', 'Start'),
      // Disabled when the current chat is already a fresh/empty one.
      disabled: isChatEmpty,
      onClick: () => {
        const id = newChat();
        if (onMainPage) {
          void navigate({ to: '/ai-cio', search: { c: id } });
          setAiCioPageHistoryOpen(false);
        } else {
          openAiPanel();
          setAiPanelShowHistory(false);
        }
      },
    },
    {
      id: 'recentChats',
      icon: MessagesSquare,
      label: t('panel.recentChats', 'Recent chats'),
      disabled: false,
      onClick: () => {
        if (onMainPage) {
          setAiCioPageHistoryOpen(true);
        } else {
          openAiPanel();
          setAiPanelShowHistory(true);
        }
      },
    },
    onMainPage
      ? {
          id: 'shrink',
          icon: Shrink,
          label: t('panel.shrink', 'Shrink'),
          disabled: false,
          onClick: () => {
            // Per spec: Shrink from the Main page opens the Dashboard with the
            // panel active. If History was open on the page, carry it into the
            // panel (History Conflict Resolution).
            // Navigate only — AppShell's syncActiveTab repoints the current tab
            // in place, so no extra tab is spawned.
            const historyWasOpen = useUIStore.getState().aiCioPageHistoryOpen;
            setAiCioPageHistoryOpen(false);
            void navigate({ to: '/dashboard' });
            openAiPanel();
            setAiPanelShowHistory(historyWasOpen);
          },
        }
      : {
          id: 'widen',
          icon: Expand,
          label: t('panel.widen', 'Widen'),
          disabled: false,
          onClick: () => {
            // Handled by the Link below; placeholder keeps the array shape.
          },
        },
  ];

  return (
    <div
      className="relative z-50"
      data-ai-cio-surface
      onMouseEnter={handleMainEnter}
      onMouseLeave={scheduleClose}
    >
      <AnimatePresence>
        {showSub &&
          subButtons.map((btn, i) =>
            !onMainPage && btn.id === 'widen' ? (
              <ExpandSubButton
                key={btn.id}
                index={i}
                onEnter={cancelClose}
                onLeave={scheduleClose}
              />
            ) : (
              <SubButton
                key={btn.id}
                index={i}
                icon={btn.icon}
                label={btn.label}
                disabled={btn.disabled}
                onClick={btn.onClick}
                onEnter={cancelClose}
                onLeave={scheduleClose}
              />
            ),
          )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={handleMainClick}
        className="relative flex size-9 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0px_1px_1.5px_rgba(0,0,0,0.1),0px_1px_1px_rgba(0,0,0,0.06)] transition-opacity hover:opacity-90"
        aria-label="AI CIO"
        whileTap={{ scale: 0.92 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      >
        <Sparkles className="size-4" />
      </motion.button>
    </div>
  );
}

/** The "Expand" sub-button navigates to the full AI CIO page via a router Link. */
function ExpandSubButton({
  index,
  onEnter,
  onLeave,
}: {
  index: number;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const { t } = useTranslation('ai-cio');
  const [showTooltip, setShowTooltip] = useState(false);
  const closeAiPanel = useUIStore((s) => s.closeAiPanel);

  return (
    <motion.div
      className="absolute start-0"
      style={{ top: (index + 1) * 44 }}
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.7 }}
      transition={{ delay: index * 0.06, type: 'spring', stiffness: 380, damping: 26 }}
      onMouseEnter={() => {
        setShowTooltip(true);
        onEnter();
      }}
      onMouseLeave={() => {
        setShowTooltip(false);
        onLeave();
      }}
    >
      <AnimatePresence>
        {showTooltip && (
          <motion.span
            initial={{ opacity: 0, x: 6 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 6 }}
            transition={{ duration: 0.12, ease: 'easeOut' }}
            className="pointer-events-none absolute end-full top-1/2 me-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-primary px-3 py-1.5 text-[11px] font-medium text-primary-foreground"
          >
            {t('panel.widen', 'Widen')}
          </motion.span>
        )}
      </AnimatePresence>
      <Link
        to={AI_CIO_PATH}
        onClick={closeAiPanel}
        aria-label={t('panel.widen', 'Widen')}
        className="flex size-9 items-center justify-center rounded-full border bg-background text-foreground shadow-[0px_1px_1px_rgba(0,0,0,0.05)] transition-colors hover:bg-accent"
      >
        <Expand className="size-4" />
      </Link>
    </motion.div>
  );
}
